
import { BButton, BCol, BForm, BFormGroup, BFormInput, BRow, BSpinner } from "bootstrap-vue";
import { extend, ValidationObserver, ValidationProvider } from "vee-validate";
import Vue from "vue";
import Component from "vue-class-component";
import { Prop } from "vue-property-decorator";
import PModal from "../look/PModal.vue";
import Multiselect from "vue-multiselect";
import { debounce } from "lodash";
import { IUserRequest } from "@/services";
import { IUserServicepointApproval, IUserTypeFind } from "@/entities/servicepoint_approval";

// Constantes del componente
const SEARCH_DEBOUNCE_DELAY = 500;
const MIN_SEARCH_LENGTH = 3;

@Component({
  name: "add-user-on-flow-approve-modal",
  components: {
    ValidationObserver,
    ValidationProvider,
    Multiselect,
    BForm,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BButton,
    BSpinner,
    PModal,
  },
})
export default class AddUserOnFlowApproveModal extends Vue {
  @Prop({ default: () => [] }) excludedUserIds!: number[];
  @Prop({ default: false }) loading!: boolean;
  @Prop({ required: false }) countryId?: number;
  public typeFind: IUserTypeFind | null = null;
  public userSelected: IUserServicepointApproval | null = null;
  public usersOptions: IUserServicepointApproval[] = [];
  public isSearching = false;
  public searchError: string | null = null;
  public hasSearched = false;

  $refs!: {
    addUserOnFlowApproveModal: InstanceType<typeof PModal>;
  };

  get optionsType(): IUserTypeFind[] {
    return [
      { code: "username", name: this.$t("column.username").toString() },
      { code: "email", name: this.$t("label.email").toString() },
      {
        code: "nameOrLastname",
        name: this.$t("label.name").toString() + " / " + this.$t("label.lastname").toString(),
      },
    ];
  }

  mounted(): void {
    this.setupValidationRules();
  }

  beforeDestroy(): void {
    this.debouncedSearchUser.cancel();
  }

  setupValidationRules(): void {
    extend("user_not_excluded", {
      validate: (value: IUserServicepointApproval) => {
        if (!value || !value.id) return true;
        return !this.excludedUserIds.includes(value.id);
      },
      message: () => this.$t("validation.messages.user_already_in_flow_approval") as string,
    });
  }

  initData(): void {
    this.resetForm();
  }

  resetForm(): void {
    this.typeFind = null;
    this.userSelected = null;
    this.usersOptions = [];
    this.isSearching = false;
    this.searchError = null;
    this.hasSearched = false;
  }

  async onSubmit(): Promise<void> {
    if (!this.userSelected) {
      this.makeToast("warning", this.$t("validation.messages.required_user").toString());
      return;
    }

    if (this.excludedUserIds.includes(this.userSelected.id)) {
      this.makeToast(
        "warning",
        this.$t("validation.messages.user_already_in_flow_approval").toString()
      );
      return;
    }

    try {
      this.$emit("add-user", this.userSelected);
      this.close();
      this.makeToast("success", this.$t("validation.messages.user_added_success").toString());
    } catch (error) {
      this.makeToast("danger", this.$t("messages.unexpectedError.description").toString());
    }
  }

  close(): void {
    this.$emit("close");
  }

  get userFindRules(): string {
    return this.typeFind ? "required|user_not_excluded" : "";
  }

  get isFormValid(): boolean {
    return !!(this.typeFind && this.userSelected && !this.isSearching);
  }

  get searchPlaceholder(): string {
    if (!this.typeFind) return this.$t("text.select_search_type_first").toString();

    const typeLabel = this.typeFind.name.toLowerCase();
    return this.$t("text.typeToSearch", [typeLabel]).toString();
  }

  get userDescriptionOptions(): IUserServicepointApproval[] {
    return this.usersOptions.map((user) => ({
      ...user,
      label: `${user.username} - ${user.name} ${user.lastName} (${user.email})`,
    }));
  }

  debouncedSearchUser = debounce(async (searchTerm: string) => {
    await this.searchUser(searchTerm);
  }, SEARCH_DEBOUNCE_DELAY);

  async searchUser(searchTerm: string): Promise<void> {
    if (!searchTerm || searchTerm.length < MIN_SEARCH_LENGTH) {
      this.usersOptions = [];
      this.hasSearched = false;
      return;
    }

    if (!this.typeFind) {
      this.searchError = this.$t("validation.messages.select_search_type").toString();
      return;
    }

    this.isSearching = true;
    this.searchError = null;

    try {
      let userRequest = {
        active: true,
        countryId: this.countryId,
      } as IUserRequest;

      if (this.typeFind.code === "username") {
        userRequest.username = searchTerm;
      } else if (this.typeFind.code === "email") {
        userRequest.email = searchTerm;
      } else if (this.typeFind.code === "nameOrLastname") {
        userRequest.nameOrLastname = searchTerm;
      }

      const users = await this.$services.user.getUsersPage(userRequest);
      this.usersOptions =
        users?.content.map((value) => {
          return {
            id: value.id,
            username: value.username,
            email: value.email,
            name: value.name,
            lastName: value.lastName,
          } as IUserServicepointApproval;
        }) || [];
      this.hasSearched = true;
    } catch (error) {
      this.searchError = this.$t("messages.unexpectedError.description").toString();
      this.usersOptions = [];
    } finally {
      this.isSearching = false;
    }
  }

  onChangeUser(): void {
    this.searchError = null;
  }

  onTypeChange(): void {
    this.userSelected = null;
    this.usersOptions = [];
    this.searchError = null;
    this.hasSearched = false;
  }

  makeToast(variant: string, message: string): void {
    this.$bvToast.toast(message, {
      autoHideDelay: 4000,
      variant,
      toaster: "b-toaster-bottom-center",
      noCloseButton: true,
    });
  }

  show(): void {
    (this.$refs.addUserOnFlowApproveModal as any).$refs.modal.show();
  }

  hide(): void {
    (this.$refs.addUserOnFlowApproveModal as any).$refs.modal.hide();
  }
}
